import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Building2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { sidebarMenu } from '@/shared/constant/menu.js';
import NavigationMenu from '@/shared/components/ui/NavigationMenu.jsx';
import Logo from '@/shared/components/modules/Logo.jsx';
import Select from '@/shared/components/elements/Select.jsx';
import useTenantList from '@/shared/hooks/useTenantList.js';
import useSelectedTenant from '@/shared/hooks/useSelectedTenant.js';
import useDebounce from '@/shared/hooks/useDebounce.js';
import lightLogo from '@/assets/images/Logo.png';
import whiteLogo from '@/assets/images/WhiteLogo.png';

export default function MobileSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();

  // --- Search Logic ---
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [hasEnoughItemsForSearch, setHasEnoughItemsForSearch] = useState(false);

  // --- Custom Hooks ---
  const { tenantList, isLoading } = useTenantList(debouncedSearchTerm);
  const { tenantId, setTenantId } = useSelectedTenant();

  // --- Effects ---
  useEffect(() => {
    if (!debouncedSearchTerm) {
      setHasEnoughItemsForSearch(tenantList.length > 5);
    }
  }, [tenantList.length, debouncedSearchTerm]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // --- Handlers ---
  const handleNavigation = useCallback(
    (item) => {
      navigate(item.path);
      // Close sidebar after navigation
      setTimeout(() => {
        onClose();
      }, 100);
    },
    [navigate, onClose],
  );

  const handleTenantChange = useCallback(
    (value) => {
      setTenantId(String(value));
      navigate('/');
      setTimeout(() => {
        onClose();
      }, 100);
    },
    [setTenantId, navigate, onClose],
  );

  const handleManageTenants = useCallback(() => {
    navigate('/tenant');
    setTimeout(() => {
      onClose();
    }, 100);
  }, [navigate, onClose]);

  // --- Derived State ---
  // Get the actual value to display in Select
  const displayValue = tenantId
    ? Number(tenantId)
    : tenantList.length > 0
      ? tenantList[0].value
      : null;
  const activeMenuPath = [
    '/subscription',
    '/credit',
    '/invoices',
    '/payments',
    '/billing',
  ].some((path) => location.pathname.startsWith(path))
    ? '/subscription'
    : location.pathname;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside
        className={`border-tertiary-10 fixed top-0 right-0 z-[70] flex h-full w-80 max-w-[85vw] flex-col border-l bg-white shadow-2xl transition-transform duration-300 ease-out dark:border-gray-800/50 dark:bg-gray-900 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Header */}
        <div className="border-tertiary-10 flex items-center justify-between border-b p-4 dark:border-gray-800/50">
          <Logo
            src={lightLogo}
            darkSrc={whiteLogo}
            size="md"
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                onClose();
              }, 100);
            }}
            className="w-36 cursor-pointer border-none !bg-transparent"
          />
          <button
            onClick={onClose}
            className="bg-tertiary-6 text-tertiary-80 hover:bg-tertiary-10 active:bg-tertiary-20 rounded-xl p-2 transition-all duration-200 hover:scale-110 active:scale-95 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            aria-label="بستن منو"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tenant Selector */}
        <div className="border-tertiary-10 border-b bg-slate-50 px-4 py-3 dark:border-gray-800/50 dark:bg-gray-800/30">
          <div className="mb-2 flex items-center gap-2">
            <div className="rounded-lg bg-orange-100 p-1.5 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400">
              <Building2 size={16} />
            </div>
            <span className="text-tertiary-80 text-xs font-semibold dark:text-gray-400">
              مرکز فعال
            </span>
          </div>
          <Select
            options={tenantList}
            value={displayValue}
            onChange={handleTenantChange}
            placeholder="انتخاب مرکز..."
            size="sm"
            loading={isLoading}
            emptyMessage={isLoading ? 'در حال جستجو...' : 'مرکزی یافت نشد'}
            showManageButton={true}
            onManageClick={handleManageTenants}
            manageButtonText="مدیریت مراکز"
            showSearch={hasEnoughItemsForSearch}
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 overflow-y-auto py-4">
          <NavigationMenu
            items={sidebarMenu}
            activeItem={activeMenuPath}
            onItemClick={handleNavigation}
            variant="default"
            showBadges={true}
            showActiveIndicator={true}
            isCollapsed={false}
          />
        </div>
      </aside>
    </>
  );
}

MobileSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
